import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const TIERS = [
  { value: '', label: 'All tiers' },
  { value: 'free',  label: 'Free' },
  { value: 'tier1', label: 'Starter' },
  { value: 'tier2', label: 'Professional' },
  { value: 'tier3', label: 'Scale' },
];

const STATUSES = [
  { value: '',          label: 'All statuses' },
  { value: 'active',    label: 'Active' },
  { value: 'trialing',  label: 'Trial' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired',   label: 'Expired' },
];

const TIER_LABELS = { free: 'Free trial', tier1: 'Starter', tier2: 'Professional', tier3: 'Scale' };

const s = {
  page: { maxWidth: 960, margin: '32px auto', padding: '0 24px 60px' },
  heading: { fontSize: 24, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  sub: { fontSize: 14, color: '#64748b', marginBottom: 20 },
  filterBar: {
    display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
    marginBottom: 20, padding: '14px 16px',
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
  },
  filterInput: {
    padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 7,
    fontSize: 13, color: '#374151', outline: 'none', flex: '1 1 180px', minWidth: 160,
  },
  filterSelect: {
    padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7,
    fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer',
  },
  filterBtn: {
    padding: '7px 16px', background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  clearBtn: {
    padding: '7px 12px', background: '#fff', color: '#64748b',
    border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, cursor: 'pointer',
  },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' },
  th: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 16px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { fontSize: 13, color: '#374151', padding: '12px 16px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  email: { fontWeight: 500, color: '#0f172a' },
  adminBadge: { fontSize: 10, fontWeight: 700, background: '#1e3a5f', color: '#60a5fa', padding: '2px 7px', borderRadius: 4, marginLeft: 8 },
  tierPill: (tier) => {
    const colors = {
      free:  { bg: '#f1f5f9', color: '#475569' },
      tier1: { bg: '#eff6ff', color: '#1d4ed8' },
      tier2: { bg: '#f0fdf4', color: '#15803d' },
      tier3: { bg: '#fdf4ff', color: '#7e22ce' },
    };
    const c = colors[tier] || colors.free;
    return { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.color };
  },
  tierSelect: (tier) => {
    const colors = {
      free:  { bg: '#f1f5f9', color: '#475569' },
      tier1: { bg: '#eff6ff', color: '#1d4ed8' },
      tier2: { bg: '#f0fdf4', color: '#15803d' },
      tier3: { bg: '#fdf4ff', color: '#7e22ce' },
    };
    const c = colors[tier] || colors.free;
    return {
      fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 8,
      background: c.bg, color: c.color, border: `1px solid ${c.color}33`,
      cursor: 'pointer', outline: 'none',
    };
  },
  statusPill: (st) => ({
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
    background: st === 'active' ? '#dcfce7' : st === 'trialing' ? '#fef9c3' : '#f1f5f9',
    color: st === 'active' ? '#15803d' : st === 'trialing' ? '#854d0e' : '#64748b',
  }),
  viewBtn: {
    background: '#2563eb', color: '#fff', border: 'none',
    fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
  },
  emptyRow: { textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: 13 },
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailFilter, setEmailFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [savingId, setSavingId] = useState(null);
  const navigate = useNavigate();

  const loadUsers = useCallback((email = emailFilter, tier = tierFilter, status = statusFilter) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (email.trim()) params.set('email', email.trim());
    if (tier) params.set('tier', tier);
    if (status) params.set('status', status);
    apiFetch(`/admin/users${params.toString() ? '?' + params : ''}`)
      .then((d) => setUsers(d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  useEffect(() => { loadUsers('', '', ''); }, []); // eslint-disable-line

  function handleSearch() { loadUsers(emailFilter, tierFilter, statusFilter); }

  function handleClear() {
    setEmailFilter(''); setTierFilter(''); setStatusFilter('');
    loadUsers('', '', '');
  }

  // Inline tier override (admin comp / testing). Paid tiers are forced to
  // 'active' because get_tier() masks a 'trialing' account back to the trial
  // tier; 'free' drops to 'cancelled'. Note: comped accounts have no Stripe
  // customer, so their Billing portal buttons will 400 — expected.
  async function changeTier(user, newTier) {
    if (newTier === (user.subscription_tier || 'free')) return;
    const label = TIER_LABELS[newTier] || newTier;
    if (!window.confirm(`Change ${user.email} to ${label}? Takes effect immediately (no Stripe charge).`)) return;
    const newStatus = newTier === 'free' ? 'cancelled' : 'active';
    setSavingId(user.id);
    try {
      await apiFetch(`/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ subscription_tier: newTier, subscription_status: newStatus }),
      });
      setUsers((prev) => prev.map((u) =>
        u.id === user.id ? { ...u, subscription_tier: newTier, subscription_status: newStatus } : u
      ));
    } catch (e) {
      alert(e.message || 'Could not update tier');
    } finally {
      setSavingId(null);
    }
  }

  const isFiltered = emailFilter || tierFilter || statusFilter;

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Users</h1>
      <p style={s.sub}>{users.length} account{users.length !== 1 ? 's' : ''}{isFiltered ? ' (filtered)' : ''}</p>

      {/* Filter bar */}
      <div style={s.filterBar}>
        <input
          style={s.filterInput}
          placeholder="Search by email…"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <select style={s.filterSelect} value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
          {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select style={s.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button style={s.filterBtn} onClick={handleSearch}>Filter</button>
        {isFiltered && <button style={s.clearBtn} onClick={handleClear}>Clear</button>}
      </div>

      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Email</th>
            <th style={s.th}>Tier</th>
            <th style={s.th}>Status</th>
            <th style={s.th}>Profiles</th>
            <th style={s.th}>Vectors</th>
            <th style={s.th}>Verdicted</th>
            <th style={s.th}>Unreviewed</th>
            <th style={s.th}>Joined</th>
            <th style={s.th}>Last login</th>
            <th style={s.th}></th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={10} style={s.emptyRow}>Loading…</td></tr>
          ) : users.length === 0 ? (
            <tr><td colSpan={10} style={s.emptyRow}>No users match your filters.</td></tr>
          ) : users.map((u) => (
            <tr key={u.id}>
              <td style={s.td}>
                <span style={s.email}>{u.email}</span>
                {u.is_admin && <span style={s.adminBadge}>ADMIN</span>}
              </td>
              <td style={s.td}>
                <select
                  style={s.tierSelect(u.subscription_tier)}
                  value={u.subscription_tier || 'free'}
                  disabled={savingId === u.id}
                  onChange={(e) => changeTier(u, e.target.value)}
                  title="Change tier (admin override — no charge)"
                >
                  <option value="free">Free</option>
                  <option value="tier1">Starter</option>
                  <option value="tier2">Professional</option>
                  <option value="tier3">Scale</option>
                </select>
              </td>
              <td style={s.td}>
                <span style={s.statusPill(u.subscription_status)}>{u.subscription_status || '—'}</span>
              </td>
              <td style={s.td}>{u.profile_count}</td>
              <td style={s.td}>{u.variant_count ?? '—'}</td>
              <td style={s.td}>{u.verdicted_count ?? '—'}</td>
              <td style={s.td}>{u.cosine_count ?? '—'}</td>
              <td style={s.td}>{formatDate(u.created_at)}</td>
              <td style={s.td}>{formatDate(u.last_login_at)}</td>
              <td style={s.td}>
                <button
                  style={s.viewBtn}
                  onClick={() => navigate(`/admin/users/${u.id}`, {
                    state: { email: u.email, tier: u.subscription_tier, status: u.subscription_status, trial_ends_at: u.trial_ends_at, is_admin: u.is_admin, last_login_at: u.last_login_at },
                  })}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
